import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, OnDestroy } from '@angular/core';
import { AppliedTax, CalculateLineItemsTaxResponse, LineItemTaxes, TaxRateService } from '@galaxy/billing';
import { App, AppKey, AppPartnerService, FieldMask, FieldMaskInterface } from '@galaxy/marketplace-apps';
import { Package, PackageService } from '@vendasta/marketplace-packages';
import { Opportunity, SalesOpportunitiesService } from '@vendasta/sales-opportunities';
import { DurationInterface, LineItem, Order, Origin, Revenue, SalesOrdersService } from '@vendasta/sales-orders';
import { DurationTotal, TaxOption, UILineItem } from '@vendasta/store';
import { add } from 'date-fns';
import { BehaviorSubject, combineLatest, merge, Observable, of, Subscription, zip } from 'rxjs';
import { catchError, filter, map, publishReplay, refCount, skipWhile, switchMap, take, tap } from 'rxjs/operators';
import { convertLineItemsToBillingSummary } from '../billing-utils';
import {
  appToSalesOrderLineItem,
  OpportunityToOrderLineItems,
  OrderToOpportunityLineItems,
  packageToSalesOrderLineItem,
} from '../conversion-utils';
import { SalesOpportunityStoreService } from '../sales-opportunity-store.service';
import { AppWithRetailPricing } from '../sales-order-line-item-selector/sales-order-line-item-selector.component';
import { SalesOrderStoreService } from '../sales-order-store.service';
import { OpportunitySidePanelEventsService } from '../../sales-opportunities/opportunity-side-panel/opportunity-side-panel-events.service';

export interface PackageApiService {
  getMulti(packageIds: string[]): Observable<Package[]>;
}

export interface AppSdkService {
  getMulti(
    appKeys: AppKey[],
    partnerId: string,
    marketId: string,
    projectionFilter: FieldMaskInterface,
    includeNotEnabled?: boolean,
  ): Observable<App[]>;
}

export interface UILineItemWithEditionPricing extends UILineItem {
  possibleRevenue?: {
    [editionId: string]: {
      currencyCode: string;
      revenue: Revenue;
    };
  };
}

const INVALID_ITEM: UILineItem = {
  name: 'Invalid Item',
  iconUrl: null,
  lineItem: null,
};

@Injectable()
export class SalesOrderConfirmationService implements OnDestroy {
  partnerId: string;
  marketId: string;
  businessId: string;
  defaultDisplayCurrency: string;

  orderId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  orderId$: Observable<string> = this.orderId$$.asObservable();

  opportunityId$$: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  opportunityId$: Observable<string> = this.opportunityId$$.asObservable();

  specifiedLineItems$$: BehaviorSubject<LineItem[]> = new BehaviorSubject<LineItem[]>([]);
  specifiedLineItems$: Observable<LineItem[]> = this.specifiedLineItems$$.asObservable();

  uiLineItems$: Observable<UILineItemWithEditionPricing[]>;

  loading$$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  loading$: Observable<boolean> = this.loading$$.asObservable();

  taxOptions$: Observable<TaxOption[]>;
  contractDuration$: Observable<DurationInterface>;

  private specifiedContractStartDate$$ = new BehaviorSubject<Date>(new Date());
  specifiedContractStartDate$: Observable<Date>;

  orderOrigin$: Observable<Origin>;

  private subscriptions: Subscription[] = [];

  readonly order$: Observable<Order>;
  private readonly opportunity$: Observable<Opportunity>;
  private readonly confirmation$: Observable<Order | Opportunity>;
  readonly estimatedTotal$: Observable<DurationTotal[]>;

  constructor(
    @Inject(PackageService) private readonly packageSdk: PackageApiService,
    @Inject(AppPartnerService) private readonly appSdk: AppSdkService,
    private readonly salesOrdersSdk: SalesOrdersService,
    private readonly salesOrderStoreService: SalesOrderStoreService,
    private readonly taxRateService: TaxRateService,
    private readonly salesOpportunityStoreService: SalesOpportunityStoreService,
    private readonly salesOpportunitiesSdk: SalesOpportunitiesService,
    private readonly opportunitySidePanelEventsService: OpportunitySidePanelEventsService,
  ) {
    const orderState$ = this.orderId$.pipe(
      skipWhile((orderId) => orderId === null),
      switchMap((orderId) => this.salesOrderStoreService.getOrderState(orderId, this.businessId)),
      publishReplay(1),
      refCount(),
    );

    const opportunityState$ = this.opportunityId$.pipe(
      skipWhile((opportunityId) => opportunityId === null),
      switchMap((opportunityId) =>
        this.salesOpportunityStoreService.getOpportunityState(opportunityId, this.businessId),
      ),
      publishReplay(1),
      refCount(),
    );

    this.subscriptions.push(
      orderState$
        .pipe(
          tap((orderState) => {
            if (orderState.loading) {
              this.loading$$.next(true);
            }
          }),
        )
        .subscribe(),
    );

    this.subscriptions.push(
      opportunityState$
        .pipe(
          tap((oppState) => {
            if (oppState.loading) {
              this.loading$$.next(true);
            }
          }),
        )
        .subscribe(),
    );

    this.order$ = orderState$.pipe(
      filter((orderState) => !orderState.loading),
      map((orderState) => orderState.order),
      publishReplay(1),
      refCount(),
    );

    this.opportunity$ = opportunityState$.pipe(
      filter((oppState) => !oppState.loading),
      map((oppState) => oppState.opportunity),
      publishReplay(1),
      refCount(),
    );

    this.orderOrigin$ = this.order$.pipe(map((order) => order?.origin));

    this.confirmation$ = combineLatest([this.order$, this.opportunity$]).pipe(
      map(([order, opportunity]) => {
        if (opportunity !== null) {
          return opportunity;
        }

        return order;
      }),
    );

    this.contractDuration$ = this.confirmation$.pipe(
      map((val) => {
        const order = val as Order;
        const opportunity = val as Opportunity;
        if (order?.contractDuration) {
          return order?.contractDuration;
        }
        return opportunity?.expectedContractDuration;
      }),
    );

    const cachedContractStartDate$ = this.confirmation$.pipe(
      map((val) => {
        const order = val as Order;
        const opportunity = val as Opportunity;
        if (order?.requestedActivation) {
          return order?.requestedActivation;
        }
        if (opportunity?.expectedCloseDate) {
          return opportunity?.expectedCloseDate;
        }
        if (val && 'orderId' in val) {
          return new Date();
        } else {
          return this.getDefaultExpectedCloseDate();
        }
      }),
    );

    this.specifiedContractStartDate$ = merge([
      cachedContractStartDate$,
      this.specifiedContractStartDate$$.asObservable(),
    ]).pipe(switchMap((startDate) => startDate));

    const combinedLineItems$ = combineLatest([this.confirmation$, this.specifiedLineItems$]).pipe(
      map(([val, lineItems]) => {
        if (!val) return lineItems;
        if ('orderId' in val) return lineItems.concat(val?.lineItems);

        return lineItems.concat(OpportunityToOrderLineItems(val?.lineItems || []));
      }),
      publishReplay(1),
      refCount(),
    );

    const packages$ = combinedLineItems$.pipe(
      switchMap((lineItems) => {
        const packageLineItems = lineItems.filter((li) => !!li.packageId);
        if (packageLineItems.length > 0) {
          return this.packageSdk.getMulti(packageLineItems.map((li) => li.packageId));
        }
        return of([]);
      }),
      publishReplay(1),
      refCount(),
    );

    const appsAndAddons$ = combinedLineItems$.pipe(
      switchMap((lineItems) => {
        const appsAndAddonsLineItems = lineItems.filter((li) => !!li.appKey);
        const appsAndAddonsKeys = appsAndAddonsLineItems.map((app) => new AppKey(app.appKey));
        if (appsAndAddonsKeys.length > 0) {
          return this.appSdk.getMulti(
            appsAndAddonsKeys,
            this.partnerId,
            this.marketId,
            new FieldMask({ paths: ['billingId'] }),
            true,
          );
        }
        return of([]);
      }),
      map((apps) => apps.filter((a) => !!a?.key?.appId)),
      publishReplay(1),
      refCount(),
    );

    this.uiLineItems$ = zip(combinedLineItems$, packages$, appsAndAddons$).pipe(
      map(([lineItems, packages, appsAndAddons]) => {
        return lineItems.map((li) => {
          let uiLineItem = INVALID_ITEM;
          if (li.packageId) {
            const pack = packages.find((p) => p.packageId === li.packageId);
            uiLineItem = this.packageToUILineItem(pack);
          } else {
            const app = appsAndAddons.find(
              (a) => a.key.appId === li.appKey.appId && (a.key.editionId || '') === (li.appKey.editionId || ''),
            );
            if (app) {
              uiLineItem = this.appWithPossibleRevenueToUILineItem(app);
            }
          }
          uiLineItem.lineItem = li;
          return uiLineItem;
        });
      }),
    );

    this.taxOptions$ = combineLatest([this.order$, this.uiLineItems$]).pipe(
      switchMap(([order, lineItems]) => {
        if (order) {
          const shouldTaxApplyToAllSKUs = (lineItems || []).every(
            (li) => (li?.lineItem?.taxRateIds || []).length === 0,
          );
          const taxOptions = (order.taxOptions || []).map((opt) => {
            const applicableSKUs = shouldTaxApplyToAllSKUs
              ? lineItems.map((li) => li.billingId)
              : lineItems
                  .filter((li) => (li?.lineItem?.taxRateIds || []).includes(opt.taxRateId))
                  .map((li) => li.billingId);
            return {
              label: opt.label,
              percentageMultiplier: opt.percentageMultiplier,
              initialAmount: opt.initialAmount,
              taxRateId: opt.taxRateId,
              applicableSkus: applicableSKUs,
            };
          });
          return of(taxOptions);
        }

        return this.taxRateService
          .calculateLineItems(
            this.partnerId,
            this.businessId,
            Object.values(convertLineItemsToBillingSummary(lineItems)),
          )
          .pipe(
            catchError((err: HttpErrorResponse) => {
              console.error(err);
              return [];
            }),
            map((response: CalculateLineItemsTaxResponse) => {
              return response.appliedTaxes.map((rate: AppliedTax) => {
                const applicableSkus = this.buildApplicableSkus(rate, response.lineItemTaxes);

                return <TaxOption>{
                  label: rate.name,
                  percentageMultiplier: rate.percentage / 100,
                  initialAmount: rate.amount,
                  taxRateId: rate.taxRateId,
                  applicableSkus: applicableSkus,
                };
              });
            }),
          );
      }),
    );

    this.estimatedTotal$ = combineLatest([this.confirmation$, this.taxOptions$]).pipe(
      map(([confirmation, taxOption]) =>
        calculateOpportunityEstimatedTotal(confirmation, taxOption, this.defaultDisplayCurrency),
      ),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.salesOrderStoreService.clearOrderStateCache();
    this.salesOpportunityStoreService.clearCache();
  }

  buildApplicableSkus(rate: AppliedTax, lineItemTaxes: LineItemTaxes[]): string[] {
    return lineItemTaxes
      .filter((liTax) => liTax.taxRateIds.some((taxRateId) => taxRateId === rate.taxRateId))
      .map((liTax) => liTax.sku);
  }

  updateLineItems(lineItems: LineItem[]): Observable<boolean> {
    this.loading$$.next(true);

    return this.confirmation$.pipe(
      take(1),
      switchMap((val) => {
        const order = val as Order;
        const opportunity = val as Opportunity;

        if (opportunity.opportunityId) {
          return this.salesOpportunitiesSdk
            .updateLineItemsForOpportunity(
              opportunity.opportunityId,
              opportunity.accountGroupId,
              opportunity.salesPersonId,
              OrderToOpportunityLineItems(lineItems),
            )
            .pipe(
              map((updatedOpportunity: Opportunity) => {
                this.salesOpportunityStoreService.refreshOpportunity(
                  updatedOpportunity.opportunityId,
                  updatedOpportunity.accountGroupId,
                );
                this.opportunitySidePanelEventsService.opportunityUpdated(updatedOpportunity);
                return true;
              }),
            );
        }

        return this.salesOrdersSdk.updateLineItems(order.orderId, order.businessId, lineItems).pipe(
          map(() => {
            this.salesOrderStoreService.refreshOrder(order.orderId, order.businessId);
            return true;
          }),
        );
      }),
    );
  }

  packageToUILineItem(pkg: Package): UILineItem {
    return {
      lineItem: packageToSalesOrderLineItem(pkg),
      billingId: pkg.packageId,
      name: pkg.name,
      iconUrl: pkg.icon,
      bannerUrl: pkg.headerImageUrl,
      isMultiActivatable: false,
      description: pkg.tagline,
    };
  }

  appWithPossibleRevenueToUILineItem(app: AppWithRetailPricing): UILineItem {
    const uiLineItem: UILineItemWithEditionPricing = {
      lineItem: appToSalesOrderLineItem(app, this.defaultDisplayCurrency),
      name: app.sharedMarketingInformation.name,
      editionName: app.sharedMarketingInformation.editionName,
      iconUrl: app.sharedMarketingInformation.iconUrl,
      bannerUrl: app.sharedMarketingInformation.bannerImageUrl,
      billingId: app.externalIdentifiers?.billingId,
      description: app.sharedMarketingInformation.tagline,
      appType: app.appType,
    };

    if (app.activationInformation) {
      uiLineItem.isMultiActivatable = app.activationInformation.multipleActivationsEnabled;
    }

    if (app.retailPricing) {
      uiLineItem.lineItem.currentRevenue = app.retailPricing.revenue;
      uiLineItem.lineItem.currencyCode = app.retailPricing.currencyCode;
    }

    if (app.parentRequirements?.enabled) {
      uiLineItem.parentIconUrl = app.parentRequirements.parentDetails.iconUrl;
      uiLineItem.parentName = app.parentRequirements.parentDetails.name;
    }
    return uiLineItem;
  }

  initialize(
    lineItems: LineItem[],
    orderId: string,
    opportunityId: string,
    partnerId: string,
    marketId: string,
    businessId: string,
    defaultDisplayCurrency: string,
  ): void {
    this.partnerId = partnerId;
    this.marketId = marketId;
    this.businessId = businessId;
    this.specifiedLineItems$$.next(lineItems || []);
    this.defaultDisplayCurrency = defaultDisplayCurrency;
    this.orderId$$.next(orderId || '');
    this.opportunityId$$.next(opportunityId || '');
  }

  addSpecifiedLineItems(lineItems: LineItem[]): void {
    this.specifiedLineItems$$.next(this.specifiedLineItems$$.getValue().concat(lineItems));
  }

  private getDefaultExpectedCloseDate(): Date {
    return add(new Date(), { months: 1 });
  }

  updateContractStartDate(date: Date): void {
    this.specifiedContractStartDate$$.next(date);
  }
}

export function calculateOpportunityEstimatedTotal(
  confirmation: Order | Opportunity,
  taxOptions: TaxOption[],
  currencyCode: string,
): DurationTotal[] {
  if (confirmation instanceof Opportunity) {
    let total = confirmation.projectedFirstYearValue / 100;
    if (taxOptions) {
      const taxTotal = taxOptions.reduce((all, tax) => all + tax.percentageMultiplier * total, 0);
      total += taxTotal;
    }
    return [
      {
        total: total,
        currencyCode: confirmation.lineItems[0]?.currencyCode || currencyCode,
        startingPrice: confirmation.lineItems.some((li) =>
          li.currentRevenue?.revenueComponents.some((rc) => rc.isStartingRevenue),
        ),
      },
    ];
  }
  return null;
}
